/**
 * EnrichmentGroupsSection - Displays enrichments in the sidebar grouped by preference
 *
 * Refactored to use the generic SidebarResourceSection and GenericResourceGroup components.
 */

import React, { useCallback } from "react";
import { Enrichment } from "@/models/enrichment";
import { useRunEnrichment, useUpdateEnrichmentGroup } from "@/api/enrichment";
import { useResourceGroups } from "@/api/resource-group";
import { useEnrichmentsStore } from "@/hooks";
import { useEnrichmentGroupBy } from "@/hooks/use-resource-group-by";
import { enrichmentGalleryConfig } from "@/config/resource-gallery-configs";
import { enrichmentSectionConfig } from "@/config/sidebar-section-configs";
import useNotif from "@/hooks/use-notif";
import SidebarResourceSection, {
  SidebarGroupWithItemsRenderProps,
} from "./sidebar-resource-section";
import GenericResourceGroup, { createUpdateItemGroupFn } from "./generic-resource-group";
import EnrichmentItem from "./enrichment-item";
import { GroupableItem } from "./resource-group-item";

// =============================================================================
// Types
// =============================================================================

interface EnrichmentGroupsSectionProps {
  /** Callback when right-clicking on a selected item (for multi-select context menu) */
  onMultiSelectContextMenu?: (e: React.MouseEvent) => void;
}

// Adapter type to make Enrichment compatible with GroupableItem
type EnrichmentGroupable = Enrichment & GroupableItem;

// =============================================================================
// Component
// =============================================================================

const EnrichmentGroupsSection: React.FC<EnrichmentGroupsSectionProps> = ({
  onMultiSelectContextMenu,
}) => {
  const { addSuccess, addError } = useNotif();

  // Fetch enrichments via store
  const { resources: enrichments } = useEnrichmentsStore();

  // Fetch resource groups for enrichments
  const { data: resourceGroups = [] } = useResourceGroups("enrichment");

  // Get groupBy preference from shared hook
  const { groupBy } = useEnrichmentGroupBy();

  // Mutations
  const runEnrichmentMutation = useRunEnrichment();
  const updateEnrichmentGroupMutation = useUpdateEnrichmentGroup();

  // Create the updateItemGroup function for GenericResourceGroup
  const updateItemGroup = useCallback(
    async (itemId: string, groupId: string) => {
      await updateEnrichmentGroupMutation.mutateAsync({
        group: { id: groupId },
        uriParams: { id: itemId },
      });
    },
    [updateEnrichmentGroupMutation.mutateAsync]
  );

  // Handle run enrichment
  const handleRunEnrichment = useCallback(
    async (enrichment: Enrichment) => {
      try {
        await runEnrichmentMutation.mutateAsync({
          uriParams: { enrichment_id: enrichment.id },
        });
        addSuccess("Enrichissement exécuté avec succès");
      } catch (error) {
        addError("Erreur lors de l'exécution de l'enrichissement");
      }
    },
    [runEnrichmentMutation, addSuccess, addError]
  );

  // Render a single enrichment item
  const renderEnrichmentItem = useCallback(
    (
      enrichment: Enrichment,
      isSelected: boolean,
      orderedIds: string[],
      multiSelectContextMenu?: (e: React.MouseEvent) => void,
      onDropToCreateGroup?: (draggedId: string, targetId: string) => void
    ) => (
      <EnrichmentItem
        key={enrichment.id}
        enrichment={enrichment}
        onRunEnrichment={handleRunEnrichment}
        isSelected={isSelected}
        orderedIds={orderedIds}
        onMultiSelectContextMenu={multiSelectContextMenu}
        onDropToCreateGroup={onDropToCreateGroup}
      />
    ),
    [handleRunEnrichment]
  );

  // Render a group using GenericResourceGroup
  const renderGroup = useCallback(
    (props: SidebarGroupWithItemsRenderProps<Enrichment>) => (
      <GenericResourceGroup<EnrichmentGroupable>
        key={props.groupKey}
        groupKey={props.groupKey}
        items={props.items as EnrichmentGroupable[]}
        groupBy={props.groupBy}
        resourceType="enrichment"
        renderItem={renderEnrichmentItem}
        getGroupIcon={enrichmentSectionConfig.getGroupIcon}
        resourceGroups={resourceGroups}
        allItems={enrichments as EnrichmentGroupable[]}
        updateItemGroup={updateItemGroup}
        orderedIds={props.orderedIds}
        onMultiSelectContextMenu={props.onMultiSelectContextMenu}
      />
    ),
    [renderEnrichmentItem, resourceGroups, enrichments, updateItemGroup]
  );

  // Render simple item (for ungrouped mode)
  const renderItem = useCallback(
    (props: {
      item: Enrichment;
      isSelected: boolean;
      orderedIds: string[];
      onMultiSelectContextMenu?: (e: React.MouseEvent) => void;
    }) => (
      <EnrichmentItem
        key={props.item.id}
        enrichment={props.item}
        onRunEnrichment={handleRunEnrichment}
        isSelected={props.isSelected}
        orderedIds={props.orderedIds}
        onMultiSelectContextMenu={props.onMultiSelectContextMenu}
      />
    ),
    [handleRunEnrichment]
  );

  return (
    <SidebarResourceSection<Enrichment>
      items={enrichments}
      config={enrichmentGalleryConfig}
      renderItem={renderItem}
      renderGroup={renderGroup}
      emptyMessage={enrichmentSectionConfig.emptyMessage}
      noResultsMessage={enrichmentSectionConfig.noResultsMessage}
      searchPlaceholder={enrichmentSectionConfig.searchPlaceholder}
      onMultiSelectContextMenu={onMultiSelectContextMenu}
      externalGroupBy={groupBy}
    />
  );
};

EnrichmentGroupsSection.displayName = "EnrichmentGroupsSection";

export default EnrichmentGroupsSection;
